import React, { useState, useEffect, useMemo } from 'react';
import { ScrollText, Download, Search } from 'lucide-react';
import { todayLocal } from '../../utils/localDate';

interface ChangelogEntry {
  _id: string;
  deploymentNumber?: number;
  title: string;
  description?: string;
  deploymentType?: string;
  priority?: string;
  modules?: Array<{ name: string }>;
  institutes?: Array<{ name: string }>;
  deployment?: { deployedAt?: string; deployedBy?: { name?: string } };
}

const TYPE_COLORS: Record<string, string> = {
  bug: 'bg-rose-100 text-rose-700',
  feature: 'bg-emerald-100 text-emerald-700',
  payload: 'bg-sky-100 text-sky-700',
  version: 'bg-violet-100 text-violet-700',
  'new-deployment': 'bg-indigo-100 text-indigo-700',
  'db-script': 'bg-amber-100 text-amber-700',
};

const ChangelogView: React.FC = () => {
  const [deployments, setDeployments] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDeployed();
  }, []);

  const fetchDeployed = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/deployments/all');
      if (!response.ok) throw new Error('Failed to fetch deployments');
      const data = await response.json();
      const deployed = (data || [])
        .filter((d: any) => d.status === 'deployed' && d.deployment?.deployedAt)
        .sort((a: any, b: any) => new Date(b.deployment.deployedAt).getTime() - new Date(a.deployment.deployedAt).getTime());
      setDeployments(deployed);
    } catch (err) {
      console.error('Error fetching changelog:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return deployments;
    const q = search.toLowerCase();
    return deployments.filter(d =>
      d.title.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.modules?.some(m => m.name.toLowerCase().includes(q))
    );
  }, [deployments, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, ChangelogEntry[]> = {};
    filtered.forEach(d => {
      const date = new Date(d.deployment!.deployedAt!);
      const key = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(d);
    });
    return groups;
  }, [filtered]);

  const handleExport = () => {
    const lines: string[] = ['# Changelog', ''];
    Object.entries(grouped).forEach(([month, entries]) => {
      lines.push(`## ${month}`, '');
      entries.forEach(d => {
        const date = new Date(d.deployment!.deployedAt!).toLocaleDateString();
        lines.push(`- **${d.title}** (${d.deploymentType || 'feature'}) — ${date}`);
        if (d.description) lines.push(`  ${d.description}`);
        if (d.modules?.length) lines.push(`  Modules: ${d.modules.map(m => m.name).join(', ')}`);
      });
      lines.push('');
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `changelog-${todayLocal()}.md`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-gray-700" />
            Changelog
          </h1>
          <p className="text-gray-600 mt-1">A running history of everything that's actually been deployed</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search changelog..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          <ScrollText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          No deployed items yet.
        </div>
      ) : (
        Object.entries(grouped).map(([month, entries]) => (
          <div key={month}>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">{month}</h2>
            <div className="space-y-3">
              {entries.map(d => (
                <div key={d._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {d.deploymentNumber && (
                        <span className="text-xs font-mono font-semibold text-blue-600">#{d.deploymentNumber}</span>
                      )}
                      <h3 className="font-semibold text-gray-900">{d.title}</h3>
                      {d.deploymentType && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_COLORS[d.deploymentType] || 'bg-gray-100 text-gray-700'}`}>
                          {d.deploymentType}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(d.deployment!.deployedAt!).toLocaleDateString()}
                    </span>
                  </div>
                  {d.description && <p className="text-sm text-gray-600 mb-2">{d.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    {d.modules && d.modules.length > 0 && (
                      <span>Modules: {d.modules.map(m => m.name).join(', ')}</span>
                    )}
                    {d.institutes && d.institutes.length > 0 && (
                      <span>Institutes: {d.institutes.map(i => i.name).join(', ')}</span>
                    )}
                    {d.deployment?.deployedBy?.name && <span>By: {d.deployment.deployedBy.name}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ChangelogView;
